import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Mail,
  KeyRound,
  CheckCircle2,
  ArrowLeft,
  Shield,
  ShieldCheck,
  Headphones,
  Zap,
  Lock
} from 'lucide-react';
import { forgotCustomerPassword, resetCustomerPassword } from '../services/api';

const UserForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: verify-otp, 3: new-password, 4: success
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [demoOtp, setDemoOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!email) throw new Error('Email is required.');
      const res = await forgotCustomerPassword(email);
      if (!res.success) throw new Error(res.message || 'Failed to send OTP.');
      
      setStep(2);
    } catch (err) {
      setError(err.message || 'Failed to request password reset.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTPOnly = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!otp) throw new Error('Please enter the 6-digit OTP code.');
      const res = await verifyCustomerOTP(email, otp);
      if (!res.success) throw new Error(res.message || 'Invalid or expired OTP code.');

      setStep(3);
    } catch (err) {
      setError(err.message || 'OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!newPassword) throw new Error('Please enter a new password.');
      const res = await resetCustomerPassword(email, otp, newPassword);
      if (!res.success) throw new Error(res.message || 'Password reset failed.');

      setStep(4);
    } catch (err) {
      setError(err.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Plus Jakarta Sans', sans-serif", display: 'flex', flexDirection: 'column' }}>
      
      {/* MAIN TOP SECTION (SPLIT SCREEN LEFT STAGE + RIGHT CARD MATCHING LOGIN LAYOUT 1:1) */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', minHeight: 'calc(100vh - 80px)', width: '100%', boxSizing: 'border-box' }}>
        
        {/* LEFT BRAND STAGE */}
        <div style={{
          background: '#ffffff',
          padding: '40px 24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          borderRight: '1px solid #e2e8f0',
          boxSizing: 'border-box'
        }}>
          
          {/* Ambient Red Waves Graphic Background Overlay */}
          <div style={{
            position: 'absolute',
            right: '-10%',
            top: '20%',
            width: '550px',
            height: '550px',
            backgroundImage: `radial-gradient(circle, rgba(254, 226, 226, 0.7) 0%, rgba(255, 255, 255, 0) 70%)`,
            pointerEvents: 'none',
            zIndex: 1
          }}></div>

          <div style={{ position: 'relative', zIndex: 2, maxWidth: '480px', margin: '0 auto' }}>
            
            {/* Subhead & Headline */}
            <span style={{ fontSize: '11px', fontWeight: 900, letterSpacing: '0.15em', color: '#dc2626', textTransform: 'uppercase', display: 'block', marginBottom: '12px' }}>
              ACCOUNT RECOVERY
            </span>

            <h1 style={{ fontSize: '40px', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, letterSpacing: '-0.03em', marginBottom: '20px', wordBreak: 'break-word' }}>
              Better Health.<br />
              <span style={{ color: '#dc2626' }}>Brighter</span> You.
            </h1>

            <p style={{ fontSize: '14.5px', color: '#64748b', lineHeight: 1.6, maxWidth: '420px', marginBottom: '32px' }}>
              Reset your password securely using a 6-digit OTP code to continue your red light therapy journey.
            </p>

            {/* Left 3 Benefit Badges */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '360px' }}>
              
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Zap size={18} />
                </div>
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>Clinically Proven Technology</h4>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: 1.4 }}>Trusted by athletes, wellness experts and thousands of happy customers.</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>2 Year Warranty</h4>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: 1.4 }}>Peace of mind with every purchase.</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Headphones size={18} />
                </div>
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>Dedicated Support</h4>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: 1.4 }}>We're here to help you on your wellness journey.</p>
                </div>
              </div>

            </div>

          </div>

        </div>

        {/* RIGHT CARD CONTAINER */}
        <div style={{ padding: '40px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', boxSizing: 'border-box' }}>
          
          <div style={{ width: '100%', maxWidth: '440px' }}>
            {/* Top Back Link */}
            <div style={{ marginBottom: '20px' }}>
              <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 800, color: '#dc2626', textDecoration: 'none' }}>
                <ArrowLeft size={16} />
                <span>Back to Login</span>
              </Link>
            </div>

            {/* FORGOT PASSWORD CARD */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '36px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)' }}>
              
              {step === 1 && (
                <div>
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                      <KeyRound size={28} />
                    </div>
                    <h2 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0' }}>Forgot Password?</h2>
                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Enter your email address and we'll send you a 6-digit OTP code to reset your password.</p>
                  </div>

                  {error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '10px 14px', borderRadius: '10px', marginBottom: '18px', fontSize: '12.5px', fontWeight: 700 }}>
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSendOTP} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>Email Address</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="email"
                          required
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none' }}
                        />
                        <Mail size={18} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        background: '#dc2626',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '13px',
                        fontSize: '14.5px',
                        fontWeight: 800,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        boxShadow: '0 6px 18px rgba(220, 38, 38, 0.35)'
                      }}
                    >
                      {loading ? 'Sending OTP...' : 'Send OTP Code'}
                    </button>
                  </form>
                </div>
              )}

              {step === 2 && (
                <div>
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                      <ShieldCheck size={28} />
                    </div>
                    <h2 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0' }}>Verify 6-Digit OTP</h2>
                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>We sent a verification code to <strong style={{ color: '#0f172a' }}>{email}</strong>.</p>
                  </div>

                  {error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '10px 14px', borderRadius: '10px', marginBottom: '18px', fontSize: '12.5px', fontWeight: 700 }}>
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleVerifyOTPOnly} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>6-Digit OTP Code</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. 849201"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '16px', letterSpacing: '0.12em', fontWeight: 900, textAlign: 'center', outline: 'none' }}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        background: '#dc2626',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '13px',
                        fontSize: '14.5px',
                        fontWeight: 800,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        boxShadow: '0 6px 18px rgba(220, 38, 38, 0.35)'
                      }}
                    >
                      {loading ? 'Verifying OTP...' : 'Verify OTP Code'}
                    </button>
                  </form>
                </div>
              )}

              {step === 3 && (
                <div>
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                      <Lock size={28} />
                    </div>
                    <h2 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0' }}>Set New Password</h2>
                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Create a strong new password for your account.</p>
                  </div>

                  {error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '10px 14px', borderRadius: '10px', marginBottom: '18px', fontSize: '12.5px', fontWeight: 700 }}>
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>New Password</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="password"
                          required
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none' }}
                        />
                        <Lock size={18} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        background: '#dc2626',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '13px',
                        fontSize: '14.5px',
                        fontWeight: 800,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        boxShadow: '0 6px 18px rgba(220, 38, 38, 0.35)'
                      }}
                    >
                      {loading ? 'Resetting Password...' : 'Reset Password'}
                    </button>
                  </form>
                </div>
              )}

              {step === 4 && (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                    <CheckCircle2 size={36} />
                  </div>
                  <h2 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: '0 0 10px 0' }}>Password Reset Complete!</h2>
                  <p style={{ fontSize: '13.5px', color: '#64748b', marginBottom: '28px', lineHeight: 1.5 }}>Your password has been successfully updated. You can now login with your new credentials.</p>

                  <Link
                    to="/login"
                    style={{
                      background: '#dc2626',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '13px 32px',
                      fontSize: '14px',
                      fontWeight: 800,
                      textDecoration: 'none',
                      display: 'inline-block',
                      boxShadow: '0 6px 18px rgba(220, 38, 38, 0.35)'
                    }}
                  >
                    Sign In Now
                  </Link>
                </div>
              )}

              {/* Security Data Box */}
              <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '12px', padding: '14px', marginTop: '24px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Shield size={16} />
                </div>
                <div>
                  <h4 style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>Your data is safe with us</h4>
                  <p style={{ fontSize: '11px', color: '#64748b', margin: 0, lineHeight: 1.4 }}>We use industry-standard encryption to protect your personal information.</p>
                </div>
              </div>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
};

export default UserForgotPassword;
